import { useState } from "react";
import { useTranslate } from "../../../../../language/i18n";
import { setMessage } from "../../../../../reducers/dialog/dialogAction";
import Modal from "../../../../shared/Modals/Modal";
import ChooseActionStep from "./Steps/ChooseActionStep";
import ActionFormStep from "./Steps/ActionFormStep";
import AttributesAndInstructionStep from "./Steps/AttributesAndInstructionStep";
import registerActionService from "../../../../../services/registerAction/registerActionService";
import styles from "./RegisterActionModal.module.scss";
import {
  Attribute,
  FormData,
  FormDefinition,
  MappedAttributes,
  OrderDefinition,
  Product,
  RegisterActionRequest,
} from "../../../../../services/registerAction/types";

const MAX_ATTRIBUTES = 7;
const STEP_CHOOSE_ACTION = 0;
const STEP_FORM = 1;
const STEP_ATTRIBUTES = 2;

interface RegisterActionModalProps {
  visible: boolean;
  onClose: () => void;
  inventoryId: string;
  product: Product;
  orderDefinitions: OrderDefinition[];
  onActionRegistered?: () => void;
}

const RegisterActionModal = ({
  visible,
  onClose,
  inventoryId,
  product,
  orderDefinitions,
  onActionRegistered,
}: RegisterActionModalProps) => {
  const translate = useTranslate();
  const [currentStep, setCurrentStep] = useState(STEP_CHOOSE_ACTION);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDefinition, setSelectedDefinition] = useState<OrderDefinition | null>(null);
  const [hasFormStep, setHasFormStep] = useState(false);
  const [formDefinition, setFormDefinition] = useState<FormDefinition | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);

  const handleChooseNext = (hasForm: boolean, formDef: FormDefinition | null) => {
    setHasFormStep(hasForm);
    setFormDefinition(formDef);
    setCurrentStep(STEP_FORM);
  };

  const handleFormNext = (data: FormData) => {
    setFormData(data);
    setCurrentStep(STEP_ATTRIBUTES);
  };

  const mapAttributes = (attrs: Attribute[]): MappedAttributes | null => {
    if (attrs.length === 0) return null;

    const result: MappedAttributes = {};

    attrs.forEach(attr => {
      const attributeNumber = Number(attr.id);
      const isValidAttributeNumber =
        Number.isInteger(attributeNumber) && attributeNumber >= 1 && attributeNumber <= MAX_ATTRIBUTES;

      if (isValidAttributeNumber) {
        result[`attribute${attributeNumber}`] = { id: attr.id, value: attr.value, label: attr.label };
      }
    });

    return Object.keys(result).length > 0 ? result : null;
  };

  const handleCreate = async ({ instruction, attributes }: { instruction: string; attributes: Attribute[] }) => {
    if (!selectedDefinition) return;

    setIsLoading(true);

    try {
      const body: RegisterActionRequest = {
        logisticsOrderDefinitionId: selectedDefinition.id,
        formData,
      };

      if (instruction) body.instruction = instruction;

      const mappedAttributes = mapAttributes(attributes);

      if (mappedAttributes) body.attributes = mappedAttributes;

      const response = await registerActionService.registerAction(inventoryId, body);

      if (response.isOk()) {
        setMessage(translate("ACTION_REGISTERED"));
        onClose();

        if (onActionRegistered) onActionRegistered();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = (): string => {
    if (currentStep === STEP_CHOOSE_ACTION) return translate("CHOOSE_PERFORMED_ACTION");
    if (currentStep === STEP_FORM && hasFormStep) return formDefinition?.label ?? translate("ADDITIONAL_INFO");
    return translate("REGISTER_ACTION");
  };

  const finalStep = hasFormStep ? STEP_ATTRIBUTES : STEP_FORM;

  const renderStep = () => {
    if (currentStep === STEP_CHOOSE_ACTION) {
      return (
        <ChooseActionStep
          orderDefinitions={orderDefinitions}
          selectedDefinition={selectedDefinition}
          onSelect={setSelectedDefinition}
          onNext={handleChooseNext}
          inventoryId={inventoryId}
        />
      );
    }

    if (currentStep === STEP_FORM && hasFormStep) {
      if (!formDefinition) return null;

      return <ActionFormStep formDefinition={formDefinition} onNext={handleFormNext} />;
    }

    if (currentStep === finalStep) {
      return (
        <AttributesAndInstructionStep
          product={product}
          selectedDefinition={selectedDefinition}
          onSubmit={handleCreate}
          isLoading={isLoading}
        />
      );
    }

    return null;
  };

  return (
    <Modal visible={visible} onClose={onClose} title={getTitle()}>
      <div className={styles.content}>{renderStep()}</div>
    </Modal>
  );
};

export default RegisterActionModal;
